import React from 'react';
import classNames from 'classnames';

import {
  faCamera,
  faCameraRotate,
  faChevronLeft,
  faExclamationTriangle,
} from '@fortawesome/pro-regular-svg-icons';
import { Button, Icon, Spinner, Text } from '@quality24/design-system';
import styles from './CodeScanner.module.scss';
import { useCodeScanner, OnResultFunction } from './useCodeScanner';

export type { OnResultParams, OnResultFunction } from './useCodeScanner';

export interface Props {
  className?: string;
  codeClassName?: string;
  constraints?: MediaTrackConstraints;
  fullscreen?: boolean;
  onClose?: () => void;
  scanDelay?: number;
  videoId?: string;
  videoContainerStyle?: React.CSSProperties;
  videoStyle?: React.CSSProperties;
  onResult?: OnResultFunction;
}

const computeVideoContainerStyle = (
  fullscreen: boolean,
  videoContainerStyle?: React.CSSProperties,
): React.CSSProperties => {
  const baseStyle = fullscreen
    ? {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9997,
      }
    : {};

  return {
    ...baseStyle,
    ...(videoContainerStyle || {}),
  };
};

const computeVideoStyle = (
  fullscreen: boolean,
  videoStyle?: React.CSSProperties,
): React.CSSProperties => {
  const baseStyle = fullscreen
    ? {
        width: '100%',
        height: '100%',
        objectFit: 'cover' as const,
      }
    : {};

  return {
    ...baseStyle,
    ...(videoStyle || {}),
  };
};

const DEFAULT_CONSTRAINTS = { facingMode: 'environment' };

// @TODO check if we can combine this compoent with CameraBarcodeScanner
export const CodeScanner: React.FunctionComponent<Props> = ({
  className,
  codeClassName,
  scanDelay = 1000,
  constraints,
  videoContainerStyle,
  videoStyle,
  fullscreen = false,
  onClose,
  onResult,
  videoId = 'qr-code-scanner-video',
}) => {
  const {
    videoRef,
    deviceCount,
    isVideoReady,
    isLoading,
    error,
    stopScanning,
    switchCamera,
  } = useCodeScanner({
    constraints: constraints || DEFAULT_CONSTRAINTS,
    scanDelay,
    videoId,
    onResult,
  });

  React.useEffect(() => {
    return () => {
      stopScanning();
    };
  }, [stopScanning]);

  const computedVideoContainerStyle = React.useMemo(
    () => computeVideoContainerStyle(fullscreen, videoContainerStyle),
    [fullscreen, videoContainerStyle],
  );

  const computedVideoStyle = React.useMemo(
    () => computeVideoStyle(fullscreen, videoStyle),
    [fullscreen, videoStyle],
  );

  const handleStopScanningAndClose = React.useCallback(() => {
    stopScanning();
    onClose?.();
  }, [onClose, stopScanning]);

  const handleChangeCamera = React.useCallback(() => {
    switchCamera();
  }, [switchCamera]);

  return (
    <div
      className={classNames(
        {
          [styles.container]: !fullscreen,
          [styles.fullscreenContainer]: fullscreen,
        },
        className,
      )}
      style={fullscreen ? computedVideoContainerStyle : videoContainerStyle}
    >
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        id={videoId}
        className={codeClassName}
        style={computedVideoStyle}
        playsInline
        autoPlay
        muted
      />
      {!fullscreen &&
        (!isVideoReady ? (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingContent}>
              {error ? (
                <>
                  <Icon icon={faExclamationTriangle} color="white" size="xl" />
                  <Text
                    size="lg1"
                    weight="bold"
                    color="white"
                    className={styles.errorText}
                  >
                    Erro ao acessar a câmera
                  </Text>
                  <Text
                    size="lg1"
                    color="white"
                    className={styles.errorMessage}
                  >
                    {error.message}
                  </Text>
                </>
              ) : (
                <>
                  <Spinner type="light" size="lg" />
                  <Text size="lg1" color="white" className={styles.loadingText}>
                    {isLoading
                      ? 'Carregando câmera...'
                      : 'Inicializando câmera...'}
                  </Text>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.overlay}>
            <div className={styles.overlayContent}>
              <div className={styles.codeScannerContainerIcon}>
                <Icon icon={faCamera} color="white" size="lg" />
              </div>
              <Text
                size="lg1"
                color="white"
                className={styles.codeScannerContainerText}
              >
                Aponte a câmera para o QR Code desejado
              </Text>
              <div className={styles.codeScannerContainerScanArea} />
            </div>
            {deviceCount > 1 && (
              <div className={styles.changeCameraButton}>
                <Button variant="link" onClick={handleChangeCamera} size="lg">
                  <Icon icon={faCameraRotate} color="white" size="lg" />
                </Button>
              </div>
            )}
          </div>
        ))}

      {fullscreen && (
        <div className={styles.fullscreenContent}>
          {!!onClose && (
            <div className={styles.closeButton}>
              <Button
                variant="link"
                onClick={handleStopScanningAndClose}
                size="lg"
              >
                <Icon icon={faChevronLeft} color="white" size="md2" />
              </Button>
            </div>
          )}
          {!isVideoReady ? (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingContent}>
                {error ? (
                  <>
                    <Icon
                      icon={faExclamationTriangle}
                      color="white"
                      size="xl"
                    />
                    <Text
                      size="lg1"
                      weight="bold"
                      color="white"
                      className={styles.errorText}
                    >
                      Erro ao acessar a câmera
                    </Text>
                    <Text
                      size="lg1"
                      color="white"
                      className={styles.errorMessage}
                    >
                      {error.message}
                    </Text>
                  </>
                ) : (
                  <>
                    <Spinner type="light" size="lg" />
                    <Text
                      size="lg1"
                      color="white"
                      className={styles.loadingText}
                    >
                      {isLoading
                        ? 'Carregando câmera...'
                        : 'Inicializando câmera...'}
                    </Text>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.overlay}>
              <div className={styles.overlayContent}>
                <div className={styles.codeScannerContainerIcon}>
                  <Icon icon={faCamera} color="white" size="lg" />
                </div>
                <Text
                  size="lg1"
                  color="white"
                  className={styles.codeScannerContainerText}
                >
                  Aponte a câmera para o QR Code desejado
                </Text>
                <div className={styles.codeScannerContainerScanArea} />
              </div>
              {deviceCount > 1 && (
                <div className={styles.changeCameraButton}>
                  <Button variant="link" onClick={handleChangeCamera} size="lg">
                    <Icon icon={faCameraRotate} color="white" size="lg" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CodeScanner;
